import React, { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { fireFeedbackConfetti } from "../../functions/fireFeedbackConfetti";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { setBackButtonUrl } from "../uiSlice";
import FeedbackPage from "../thankyou/FeedbackPage";

export default function Feedback() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);

  const stageId = useMemo(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      return cached?.stageId || null;
    } catch {
      return null;
    }
  }, []);

  // Check if Anchor Mode
  const isAnchorMode = useMemo(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      return cached?.isAnchorMode === true;
    } catch {
      return false;
    }
  }, []);

  const state = location.state || {};

  const uid = user?.userId ?? user?.userid ?? user?.id ?? storedUserData.userId ?? storedUserData.userid ?? storedUserData.id;
  const submittedRef = useRef(false);

  useEffect(() => {
    dispatch(setBackButtonUrl("/"));
  }, [dispatch]);

  useEffect(() => {
    document.documentElement.classList.add("thankyou-simple-bg");
    document.body.classList.add("thankyou-simple-bg");
    return () => {
      document.documentElement.classList.remove("thankyou-simple-bg");
      document.body.classList.remove("thankyou-simple-bg");
    };
  }, []);

  const handleFeedbackSelect = async (feedbackLabel) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const organizationId = storedUserData.organizationId;
    const sessionId = storedUserData.sessionId;

    if (!uid || !organizationId || !sessionId) {
      await Swal.fire("Error", "Missing required user data to save feedback", "error");
      navigate("/", { replace: true });
      return;
    }

    // Map feedback labels to backend values
    const feedbackMapping = {
      "Amazingggg": "AWESOME",
      "Loved it": "LOVED",
      "It was fun": "MINDBLOWING",
      "It was okay": "LIKED",
    };

    const winnerValue = feedbackMapping[feedbackLabel] || feedbackLabel;

    try {
      await updateGameSettings({
        token: `${sessionId}&${organizationId}`,
        userId: uid,
        stageId: stageId,
        themeName: storedUserData.themeName || user?.themeName || "guess_me",
        winner: winnerValue,
      });

      fireFeedbackConfetti();

      // For Anchor Mode: go home or dashboard. Otherwise: go to leaderboard
      if (isAnchorMode) {
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 2000);
      } else {
        // Redirect to leaderboard after confetti
        setTimeout(() => {
          navigate("/leaderboard", { replace: true, state: { sessionId, organizationId } });
        }, 2000);
      }
    } catch (e) {
      submittedRef.current = false;
      await Swal.fire("Error", e?.message || "Failed to save feedback", "error");
    }
  };

  return <FeedbackPage onSelect={handleFeedbackSelect} />;
}
